import React, { useRef, useEffect, useState, useCallback } from 'react';
import { EventItem } from '../../types/event';
import EventSummaryCard from './EventSummaryCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { logger } from '../../utils/logger';
import { User } from 'firebase/auth';
import '../../styles/EventsCarouselSection.css';

interface EventsCarouselSectionProps {
  title: string;
  events: EventItem[];
  className?: string;
  emptyMessage?: string;
  currentUser?: User | null;
}

const EventsCarouselSection: React.FC<EventsCarouselSectionProps> = ({
  title,
  events,
  className = "",
  emptyMessage = "No events found in this time period.",
  currentUser
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 576);

  // Check if we're on mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 576);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Check scroll position and update state
  const checkScrollPosition = useCallback(() => {
    if (!carouselRef.current || isMobile) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    
    // Check if at the start of the carousel (no scrolling left possible)
    setIsAtStart(scrollLeft <= 0);
    
    // Check if at the end of the carousel (no scrolling right possible)
    // Add small buffer (1px) for browser rounding errors
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    
    logger.info('📜 Carousel scroll position updated', { 
      section: title, 
      isAtStart: scrollLeft <= 0, 
      isAtEnd: scrollLeft + clientWidth >= scrollWidth - 1 
    });
  }, [isMobile, title]);

  // Add intersection observer and scroll event listener
  useEffect(() => {
    if (!carouselRef.current || events.length === 0 || isMobile) return;

    const scrollContainer = carouselRef.current;
    
    // Check initial scroll position
    checkScrollPosition();
    
    // Add scroll event listener to update button visibility
    scrollContainer.addEventListener('scroll', checkScrollPosition);
    
    // Log when carousel becomes visible for better analytics
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            logger.info('👁️ Carousel section became visible', { section: title, eventCount: events.length });
            // Check position when becoming visible
            checkScrollPosition();
          }
        });
      },
      { threshold: 0.1 } // Trigger when at least 10% is visible
    );

    observer.observe(scrollContainer);

    // Check on resize too
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [events, title, isMobile, checkScrollPosition]);

  // Format the date for display
  const formatEventDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format the time for display
  const formatEventTime = (event: EventItem): string => {
    return `${event.startTime.doorsOpen} - ${event.endTime || 'TBD'}`;
  };

  // Get the theme from description
  const getEventTheme = (description: string): string => {
    if (description.toLowerCase().includes('play-party')) return 'Play-Party';
    if (description.toLowerCase().includes('workshop')) return 'Workshop';
    if (description.toLowerCase().includes('social')) return 'Social';
    return 'Event';
  };

  // Scroll carousel left
  const scrollLeft = () => {
    if (!carouselRef.current || isMobile) return;
    const scrollAmount = carouselRef.current.clientWidth * 0.75;
    carouselRef.current.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
    logger.info('◀️ Carousel scrolled left', { section: title });
  };

  // Scroll carousel right
  const scrollRight = () => {
    if (!carouselRef.current || isMobile) return;
    const scrollAmount = carouselRef.current.clientWidth * 0.75;
    carouselRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
    logger.info('▶️ Carousel scrolled right', { section: title });
  };

  // Only render the section if there are events
  if (events.length === 0) {
    return null;
  }
  
  return (
    <section className={`ecs-section ${className}`}>
      <div className="ecs-header">
        <h2 className="ecs-title">{title}</h2>
      </div>
      
      <div className={`ecs-carousel-container ${isAtStart ? 'at-start' : ''} ${isAtEnd ? 'at-end' : ''} ${isMobile ? 'mobile-view' : ''}`}>
        {!isAtStart && !isMobile && (
          <button 
            className="ecs-scroll-button ecs-scroll-left"
            onClick={scrollLeft}
            aria-label="Scroll left"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        )}
        
        <div className={`ecs-carousel ${isMobile ? 'mobile-grid' : ''}`} ref={carouselRef}>
          {events.length > 0 ? (
            events.map(event => (
              <div className="ecs-card-wrapper" key={event.id}>
                <EventSummaryCard
                  title={event.title}
                  description={event.description}
                  imageSrc={event.imageUrl || '/images/room_events.jpg'} // Fallback image
                  theme={getEventTheme(event.description)}
                  linkHref={`/events/${event.id}`} // Link to individual event page
                  linkText={`Join us on ${formatEventDate(event.date)}`}
                  date={formatEventDate(event.date)}
                  time={formatEventTime(event)}
                  location={event.address}
                  eventId={event.id}
                  showDescription={false}
                  currentUser={currentUser}
                />
              </div>
            ))
          ) : (
            <p className="ecs-empty-message">{emptyMessage}</p>
          )}
        </div>
        
        {!isAtEnd && !isMobile && (
          <button 
            className="ecs-scroll-button ecs-scroll-right"
            onClick={scrollRight}
            aria-label="Scroll right"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
    </section>
  );
};

export default EventsCarouselSection;